/**
 * Deterministic world generation.
 *
 * The server never transmits the forest — it transmits a seed, and both sides
 * build a byte-identical world from it. That means the monster's AI reasons
 * about exactly the trees you can see, joining costs almost nothing, and the
 * whole world is reproducible in a unit test.
 *
 * Consequence: every function here must be a pure function of the seed. No
 * Math.random(), no Date.now(), no iteration over unordered collections.
 */

import { createNoise2D, type NoiseFunction2D } from 'simplex-noise';
import { Rng, deriveSeed } from './rng.js';
import { WORLD, TREES, TOWERS, ITEMS, HIDING, EXTRACTION } from './tuning.js';
import { Difficulty, profileFor, type DifficultyProfile } from './difficulty.js';
import { clamp, dist2d, smoothstep, TAU } from './math.js';

export interface TreeInstance {
  x: number;
  y: number;
  z: number;
  radius: number;
  height: number;
  variant: number;
  rotation: number;
  /** Slight per-tree lean, radians. */
  tilt: number;
}

export interface TowerSite {
  id: number;
  x: number;
  y: number;
  z: number;
  rotation: number;
  /** Which lore clue this tower unlocks. */
  clueIndex: number;
}

export type ItemKind = 'battery' | 'flare' | 'map' | 'compass' | 'radio' | 'booster';

/**
 * Items that work simply by being carried.
 *
 * These never occupy a hotbar slot — there is nothing to select or use, so a
 * slot would be dead weight. They are shown as carried text instead, and only
 * one of each can be held since a second confers no benefit.
 */
export const PASSIVE_ITEMS: ReadonlySet<ItemKind> = new Set<ItemKind>([
  'compass',
  'booster',
  'radio',
  'map',
]);

export function isPassiveItem(kind: string): boolean {
  return PASSIVE_ITEMS.has(kind as ItemKind);
}

export interface ItemSpawn {
  id: number;
  kind: ItemKind;
  x: number;
  y: number;
  z: number;
}

export type HidingKind = 'log' | 'thicket' | 'locker' | 'trunk';

export interface HidingSpot {
  id: number;
  kind: HidingKind;
  x: number;
  y: number;
  z: number;
  rotation: number;
}

/**
 * How many players fit in a spot of this kind.
 *
 * Derived from the kind rather than stored on the spot so it stays out of the
 * generated world description: both sides already build identical spots from
 * the seed, so both can reach the same number without it travelling anywhere.
 * The server enforces it; the client only reads it to know when to stop
 * offering the spot.
 */
export function hidingCapacity(kind: HidingKind): number {
  return HIDING.CAPACITY[kind];
}

export type LandmarkKind = 'car' | 'tent' | 'shack' | 'bus' | 'rocks';

export interface Landmark {
  id: number;
  kind: LandmarkKind;
  x: number;
  y: number;
  z: number;
  rotation: number;
}

/** Anything that carves a clearing out of the forest. */
interface Clearing {
  x: number;
  z: number;
  radius: number;
}

export class World {
  readonly seed: number;
  /**
   * Difficulty is part of the world's identity, not a setting applied on top.
   *
   * Tower, item and hiding-spot counts feed generation, so two clients on
   * different difficulties would build different forests from the same seed.
   * It travels with the seed for exactly that reason.
   */
  readonly difficulty: number;
  private readonly profile: DifficultyProfile;
  readonly trees: TreeInstance[] = [];
  readonly towers: TowerSite[] = [];
  readonly items: ItemSpawn[] = [];
  readonly hidingSpots: HidingSpot[] = [];
  readonly landmarks: Landmark[] = [];
  readonly extraction: { x: number; y: number; z: number };
  /** Where players drop in. */
  readonly spawnPoint: { x: number; y: number; z: number };

  private readonly terrainNoise: NoiseFunction2D;
  private readonly detailNoise: NoiseFunction2D;
  private readonly densityNoise: NoiseFunction2D;
  private readonly treeHash: SpatialHash<TreeInstance>;

  constructor(seed: number, difficulty: number = Difficulty.Normal) {
    this.seed = seed >>> 0;
    this.difficulty = difficulty;
    this.profile = profileFor(difficulty);

    // Independent noise streams, so tuning one system never disturbs another.
    this.terrainNoise = createNoise2D(rngFn(deriveSeed(this.seed, 'terrain')));
    this.detailNoise = createNoise2D(rngFn(deriveSeed(this.seed, 'detail')));
    this.densityNoise = createNoise2D(rngFn(deriveSeed(this.seed, 'density')));

    // Order matters and is fixed: sites first, then the forest fills in around
    // the clearings they carve.
    const clearings: Clearing[] = [];

    this.extraction = this.generateExtraction();
    clearings.push({ x: this.extraction.x, z: this.extraction.z, radius: EXTRACTION.ZONE_RADIUS + 6 });

    this.generateTowers(clearings);
    this.generateLandmarks(clearings);

    this.spawnPoint = this.generateSpawnPoint();
    clearings.push({ x: this.spawnPoint.x, z: this.spawnPoint.z, radius: 9 });

    this.generateTrees(clearings);
    this.generateHidingSpots();
    this.generateItems();

    this.treeHash = new SpatialHash<TreeInstance>(8);
    for (const t of this.trees) this.treeHash.insert(t.x, t.z, t);
  }

  // -------------------------------------------------------------------------
  // Terrain
  // -------------------------------------------------------------------------

  /**
   * Ground height at a world position. Pure, analytic, and identical on both
   * sides — the renderer's mesh, the player's feet, and the monster's pathing
   * all sample this same function.
   */
  heightAt(x: number, z: number): number {
    let amp = 1;
    let freq = WORLD.TERRAIN_FREQ;
    let sum = 0;
    let norm = 0;

    for (let o = 0; o < WORLD.TERRAIN_OCTAVES; o++) {
      sum += this.terrainNoise(x * freq, z * freq) * amp;
      norm += amp;
      amp *= 0.5;
      freq *= 2.07; // non-integer lacunarity avoids visible grid alignment
    }

    let h = (sum / norm) * WORLD.HEIGHT_SCALE;

    // Fine detail so the ground isn't glassy underfoot.
    h += this.detailNoise(x * 0.05, z * 0.05) * 0.35;

    // The rim: terrain climbs sharply past the playable radius, forming a
    // natural bowl. Cheaper and less immersion-breaking than an invisible wall.
    const r = Math.sqrt(x * x + z * z);
    const rim = smoothstep(WORLD.PLAYABLE_RADIUS * 0.9, WORLD.PLAYABLE_RADIUS * 1.3, r);
    h += rim * 62;

    return h;
  }

  /** Surface normal via central differences. */
  normalAt(x: number, z: number, eps = 0.6): { x: number; y: number; z: number } {
    const hL = this.heightAt(x - eps, z);
    const hR = this.heightAt(x + eps, z);
    const hD = this.heightAt(x, z - eps);
    const hU = this.heightAt(x, z + eps);

    const nx = hL - hR;
    const nz = hD - hU;
    const ny = 2 * eps;
    const len = Math.hypot(nx, ny, nz);
    return { x: nx / len, y: ny / len, z: nz / len };
  }

  /** Slope in radians from vertical. 0 = flat. */
  slopeAt(x: number, z: number): number {
    const n = this.normalAt(x, z);
    return Math.acos(clamp(n.y, -1, 1));
  }

  /** True if a position is inside the playable bowl. */
  isInBounds(x: number, z: number): boolean {
    return x * x + z * z < WORLD.PLAYABLE_RADIUS * WORLD.PLAYABLE_RADIUS;
  }

  // -------------------------------------------------------------------------
  // Spatial queries (used for collision, line-of-sight, and monster smashing)
  // -------------------------------------------------------------------------

  /** Trees whose centre lies within `radius` of the point. */
  treesNear(x: number, z: number, radius: number): TreeInstance[] {
    return this.treeHash.query(x, z, radius);
  }

  /**
   * How much tree cover sits between two points, as 0 (clear) to 1 (hidden).
   *
   * This is deliberately *not* a binary line-of-sight test. At this tree
   * density (roughly one trunk per 20 m²) a 40 m sightline clips a trunk
   * essentially always, so a boolean check reports "blocked" for every pair of
   * points and the monster goes permanently blind.
   *
   * Instead each trunk near the ray contributes partial cover that saturates
   * with distance. The result reads the way a forest actually behaves: you are
   * plainly visible across a clearing, hard to pick out at range through dense
   * timber, and cover buys you progressively more the further away you are.
   */
  sightOcclusion(ax: number, az: number, bx: number, bz: number): number {
    const dx = bx - ax;
    const dz = bz - az;
    const len = Math.hypot(dx, dz);
    if (len < 0.001) return 0;

    const ux = dx / len;
    const uz = dz / len;

    let cover = 0;
    const step = 6;
    const seen = new Set<TreeInstance>();

    for (let d = 0; d < len; d += step) {
      const px = ax + ux * d;
      const pz = az + uz * d;
      const span = Math.min(step, len - d);

      for (const tree of this.treeHash.query(px, pz, span * 0.5 + 2)) {
        if (seen.has(tree)) continue;

        // Perpendicular distance from the trunk centre to the segment.
        const tx = tree.x - ax;
        const tz = tree.z - az;
        const proj = tx * ux + tz * uz;
        if (proj < 0 || proj > len) continue;

        const cx = ax + ux * proj - tree.x;
        const cz = az + uz * proj - tree.z;
        const perp = Math.hypot(cx, cz);

        // A trunk hides roughly its own width plus a little penumbra. Trunks
        // near the far end of the ray screen the target more effectively than
        // ones right next to the observer.
        const halfWidth = tree.radius + 0.25;
        if (perp > halfWidth) continue;

        seen.add(tree);
        const centrality = 1 - perp / halfWidth;
        const depth = 0.35 + 0.65 * (proj / len);
        cover += centrality * depth;
      }
    }

    // Saturating response: the first trunk matters a lot, the tenth barely.
    return 1 - Math.exp(-1.15 * cover);
  }

  /** Convenience boolean for callers that genuinely want a hard yes/no. */
  isSightBlocked(ax: number, az: number, bx: number, bz: number): boolean {
    return this.sightOcclusion(ax, az, bx, bz) > 0.85;
  }

  // -------------------------------------------------------------------------
  // Generation passes
  // -------------------------------------------------------------------------

  private generateExtraction(): { x: number; y: number; z: number } {
    const rng = new Rng(deriveSeed(this.seed, 'extraction'));
    // A clearing out toward the rim, so reaching it is a real journey.
    const angle = rng.range(0, TAU);
    const dist = WORLD.PLAYABLE_RADIUS * rng.range(0.62, 0.8);
    const x = Math.cos(angle) * dist;
    const z = Math.sin(angle) * dist;
    return { x, y: this.heightAt(x, z), z };
  }

  private generateSpawnPoint(): { x: number; y: number; z: number } {
    const rng = new Rng(deriveSeed(this.seed, 'spawn'));
    // Opposite side of the map from extraction, near the centre.
    const away = Math.atan2(this.extraction.z, this.extraction.x) + Math.PI;
    for (let attempt = 0; attempt < 40; attempt++) {
      const angle = away + rng.range(-0.9, 0.9);
      const dist = rng.range(30, 90);
      const x = Math.cos(angle) * dist;
      const z = Math.sin(angle) * dist;
      if (this.slopeAt(x, z) < 0.3) return { x, y: this.heightAt(x, z), z };
    }
    return { x: 0, y: this.heightAt(0, 0), z: 0 };
  }

  private generateTowers(clearings: Clearing[]): void {
    const rng = new Rng(deriveSeed(this.seed, 'towers'));

    // Rejection sampling: high, flat ground, well separated, away from the
    // extraction zone so the map doesn't collapse into one corner.
    let attempts = 0;
    while (this.towers.length < this.profile.towerCount && attempts < 6000) {
      attempts++;
      const angle = rng.range(0, TAU);
      const dist = WORLD.PLAYABLE_RADIUS * Math.sqrt(rng.range(0.02, 0.86));
      const x = Math.cos(angle) * dist;
      const z = Math.sin(angle) * dist;

      if (this.slopeAt(x, z) > 0.24) continue;
      if (dist2d(x, z, this.extraction.x, this.extraction.z) < 70) continue;

      let tooClose = false;
      for (const t of this.towers) {
        if (dist2d(x, z, t.x, t.z) < TOWERS.MIN_SEPARATION) {
          tooClose = true;
          break;
        }
      }
      if (tooClose) continue;

      this.towers.push({
        id: this.towers.length,
        x,
        y: this.heightAt(x, z),
        z,
        rotation: rng.range(0, TAU),
        clueIndex: this.towers.length,
      });
      clearings.push({ x, z, radius: TREES.CLEARING_RADIUS });
    }

    // Relax the separation requirement if the terrain was uncooperative.
    let relaxed = TOWERS.MIN_SEPARATION;
    while (this.towers.length < this.profile.towerCount && relaxed > 30) {
      relaxed *= 0.8;
      for (let i = 0; i < 2000 && this.towers.length < this.profile.towerCount; i++) {
        const angle = rng.range(0, TAU);
        const dist = WORLD.PLAYABLE_RADIUS * Math.sqrt(rng.range(0.02, 0.86));
        const x = Math.cos(angle) * dist;
        const z = Math.sin(angle) * dist;
        if (this.slopeAt(x, z) > 0.34) continue;
        let ok = true;
        for (const t of this.towers) {
          if (dist2d(x, z, t.x, t.z) < relaxed) {
            ok = false;
            break;
          }
        }
        if (!ok) continue;
        this.towers.push({
          id: this.towers.length,
          x,
          y: this.heightAt(x, z),
          z,
          rotation: rng.range(0, TAU),
          clueIndex: this.towers.length,
        });
        clearings.push({ x, z, radius: TREES.CLEARING_RADIUS });
      }
    }
  }

  private generateLandmarks(clearings: Clearing[]): void {
    const rng = new Rng(deriveSeed(this.seed, 'landmarks'));
    const kinds: LandmarkKind[] = ['car', 'tent', 'shack', 'bus', 'rocks'];
    const count = 14;

    let attempts = 0;
    while (this.landmarks.length < count && attempts < 4000) {
      attempts++;
      const angle = rng.range(0, TAU);
      const dist = WORLD.PLAYABLE_RADIUS * Math.sqrt(rng.next()) * 0.92;
      const x = Math.cos(angle) * dist;
      const z = Math.sin(angle) * dist;

      if (this.slopeAt(x, z) > 0.28) continue;

      let tooClose = false;
      for (const c of clearings) {
        if (dist2d(x, z, c.x, c.z) < c.radius + 12) {
          tooClose = true;
          break;
        }
      }
      if (tooClose) continue;

      this.landmarks.push({
        id: this.landmarks.length,
        kind: rng.pick(kinds),
        x,
        y: this.heightAt(x, z),
        z,
        rotation: rng.range(0, TAU),
      });
      clearings.push({ x, z, radius: 7 });
    }
  }

  private generateTrees(clearings: Clearing[]): void {
    const rng = new Rng(deriveSeed(this.seed, 'trees'));
    const half = WORLD.PLAYABLE_RADIUS * 1.12;
    const spacing = TREES.GRID_SPACING;
    const jitter = spacing * TREES.JITTER;

    // A jittered grid gives even coverage without Poisson-disc's cost, and the
    // per-cell noise gate carves natural clearings and thickets.
    for (let gx = -half; gx <= half; gx += spacing) {
      for (let gz = -half; gz <= half; gz += spacing) {
        const x = gx + rng.range(-jitter * 0.5, jitter * 0.5);
        const z = gz + rng.range(-jitter * 0.5, jitter * 0.5);

        const r = Math.hypot(x, z);
        if (r > WORLD.PLAYABLE_RADIUS * 1.1) continue;

        // Beyond the playable radius, pack trees in tight to form a visual wall.
        const isRim = r > WORLD.PLAYABLE_RADIUS * 0.96;

        if (!isRim) {
          const density = this.densityNoise(x * TREES.DENSITY_FREQ, z * TREES.DENSITY_FREQ);
          if (density < TREES.DENSITY_THRESHOLD) continue;
          if (this.slopeAt(x, z) > TREES.MAX_SLOPE) continue;

          let inClearing = false;
          for (const c of clearings) {
            if (dist2d(x, z, c.x, c.z) < c.radius) {
              inClearing = true;
              break;
            }
          }
          if (inClearing) continue;
        }

        const scale = rng.range(0.75, 1.25);
        this.trees.push({
          x,
          y: this.heightAt(x, z),
          z,
          radius: rng.range(TREES.MIN_RADIUS, TREES.MAX_RADIUS) * scale,
          height: rng.range(TREES.MIN_HEIGHT, TREES.MAX_HEIGHT) * scale,
          variant: rng.int(0, TREES.VARIANTS - 1),
          rotation: rng.range(0, TAU),
          tilt: rng.gaussian() * 0.035,
        });
      }
    }
  }

  private generateHidingSpots(): void {
    const rng = new Rng(deriveSeed(this.seed, 'hiding'));

    // Every landmark that can plausibly conceal a person becomes a spot, so
    // hiding places are where players intuitively look for them.
    for (const lm of this.landmarks) {
      const kind: HidingKind | null =
        lm.kind === 'car' || lm.kind === 'bus' ? 'trunk' : lm.kind === 'shack' ? 'locker' : null;
      if (!kind) continue;
      this.hidingSpots.push({
        id: this.hidingSpots.length,
        kind,
        x: lm.x + Math.cos(lm.rotation) * 2.4,
        y: lm.y,
        z: lm.z + Math.sin(lm.rotation) * 2.4,
        rotation: lm.rotation,
      });
    }

    // A maintenance locker at the foot of each tower — a deliberate escape
    // valve for the moment after you finish transmitting.
    for (const tower of this.towers) {
      const a = rng.range(0, TAU);
      this.hidingSpots.push({
        id: this.hidingSpots.length,
        kind: 'locker',
        x: tower.x + Math.cos(a) * 6.5,
        y: tower.y,
        z: tower.z + Math.sin(a) * 6.5,
        rotation: a + Math.PI,
      });
    }

    // Scatter the remainder as hollow logs and thickets out in the forest.
    let attempts = 0;
    while (this.hidingSpots.length < this.profile.hidingCount && attempts < 4000) {
      attempts++;
      const angle = rng.range(0, TAU);
      const dist = WORLD.PLAYABLE_RADIUS * Math.sqrt(rng.next()) * 0.93;
      const x = Math.cos(angle) * dist;
      const z = Math.sin(angle) * dist;
      if (this.slopeAt(x, z) > 0.3) continue;

      let ok = true;
      for (const h of this.hidingSpots) {
        if (dist2d(x, z, h.x, h.z) < 30) {
          ok = false;
          break;
        }
      }
      if (!ok) continue;

      this.hidingSpots.push({
        id: this.hidingSpots.length,
        kind: rng.bool(0.55) ? 'log' : 'thicket',
        x,
        y: this.heightAt(x, z),
        z,
        rotation: rng.range(0, TAU),
      });
    }
  }

  private generateItems(): void {
    const rng = new Rng(deriveSeed(this.seed, 'items'));

    // Anchor most loot to landmarks and tower bases so exploring meaningful
    // places pays off, rather than sweeping empty forest.
    const anchors: Array<{ x: number; z: number }> = [
      ...this.landmarks.map((l) => ({ x: l.x, z: l.z })),
      ...this.towers.map((t) => ({ x: t.x, z: t.z })),
    ];

    const place = (kind: ItemKind): void => {
      for (let attempt = 0; attempt < 60; attempt++) {
        let x: number;
        let z: number;

        if (anchors.length > 0 && rng.bool(0.72)) {
          const a = rng.pick(anchors);
          const angle = rng.range(0, TAU);
          const d = rng.range(1.5, 7);
          x = a.x + Math.cos(angle) * d;
          z = a.z + Math.sin(angle) * d;
        } else {
          const angle = rng.range(0, TAU);
          const dist = WORLD.PLAYABLE_RADIUS * Math.sqrt(rng.next()) * 0.9;
          x = Math.cos(angle) * dist;
          z = Math.sin(angle) * dist;
        }

        if (!this.isInBounds(x, z)) continue;
        if (this.slopeAt(x, z) > 0.36) continue;

        this.items.push({
          id: this.items.length,
          kind,
          x,
          y: this.heightAt(x, z),
          z,
        });
        return;
      }
    };

    // Fixed key order — object iteration order must not vary between runtimes.
    const order: ItemKind[] = ['battery', 'flare', 'map', 'compass', 'radio', 'booster'];
    for (const kind of order) {
      // Scaled by difficulty. Rounded rather than floored so a sparse
      // profile never removes a category entirely, and identical on both
      // sides because it is pure arithmetic on shared inputs.
      const count = Math.max(1, Math.round(ITEMS.SPAWN_COUNTS[kind] * this.profile.itemCount));
      for (let i = 0; i < count; i++) place(kind);
    }
  }
}

// ---------------------------------------------------------------------------
// Spatial hash
// ---------------------------------------------------------------------------

/**
 * Uniform-grid spatial hash for XZ point queries. Used for tree collision,
 * line-of-sight, and the monster's smash checks — all of which run every tick
 * and would be unusable as linear scans over thousands of trees.
 */
export class SpatialHash<T> {
  private readonly cellSize: number;
  private readonly cells = new Map<number, T[]>();

  constructor(cellSize: number) {
    this.cellSize = cellSize;
  }

  private key(cx: number, cz: number): number {
    // Pack two 16-bit cell coords into one number. World is far smaller than
    // the +/-32k this allows.
    return ((cx + 32768) << 16) | (cz + 32768);
  }

  insert(x: number, z: number, value: T): void {
    const cx = Math.floor(x / this.cellSize);
    const cz = Math.floor(z / this.cellSize);
    const k = this.key(cx, cz);
    let bucket = this.cells.get(k);
    if (!bucket) {
      bucket = [];
      this.cells.set(k, bucket);
    }
    bucket.push(value);
  }

  query(x: number, z: number, radius: number): T[] {
    const minCx = Math.floor((x - radius) / this.cellSize);
    const maxCx = Math.floor((x + radius) / this.cellSize);
    const minCz = Math.floor((z - radius) / this.cellSize);
    const maxCz = Math.floor((z + radius) / this.cellSize);

    const out: T[] = [];
    for (let cx = minCx; cx <= maxCx; cx++) {
      for (let cz = minCz; cz <= maxCz; cz++) {
        const bucket = this.cells.get(this.key(cx, cz));
        if (bucket) out.push(...bucket);
      }
    }
    return out;
  }
}

/** Adapt our Rng to the plain `() => number` that simplex-noise expects. */
function rngFn(seed: number): () => number {
  const rng = new Rng(seed);
  return () => rng.next();
}

/** Cache so repeated `new World(sameSeed)` in one process is cheap. */
const worldCache = new Map<string, World>();

/**
 * Cached by seed *and* difficulty: the same seed produces different forests at
 * different difficulties, so keying on the seed alone would hand back the wrong
 * world and silently desync the two sides.
 */
export function getWorld(seed: number, difficulty: number = Difficulty.Normal): World {
  const normalizedSeed = seed >>> 0;
  const key = `${normalizedSeed}:${difficulty}`;

  let world = worldCache.get(key);
  if (!world) {
    world = new World(normalizedSeed, difficulty);
    // Only ever hold a couple — these are multi-megabyte objects.
    if (worldCache.size > 3) worldCache.clear();
    worldCache.set(key, world);
  }
  return world;
}
